import React, { useState } from 'react';
import withLayout from '../../../hocs/Layout';
import { FormProvider, RHFFileField } from '../../../common/hook-form';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { Box, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { apiConstants, postMultipart } from '../../../common/constants';
import { toast } from "react-toastify";
import { Button } from '@mui/material';
import { UploadFileOutlined } from '@mui/icons-material';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LabelDownloadBulk from "./LabelDownload"
import NestedTabTray from '../../../common/NestedTabTray';
import { FONT, SIZE } from '../../../styles/foundation/typography';
import { PRIMARY_BLUE, PURPLE, WARNING } from '../../../styles/foundation/colors';
import {
    pageContainerSx,
    uploadSectionSx, primaryBtnSx, secondaryBtnSx, helpTextSx, helpTextTypeSx,
    alertStripSx,
} from '../../../styles/patterns';
import { DownloadHero, SectionHead } from './_downloadUi';

// `embedded`: rendered as a tab of the Label & Invoices page. In that mode the two download
// criteria (by pincode & date, by AWB upload) become sub-tabs in a tray that branches from
// the active Label tab (caretX passed by the parent). Standalone keeps both stacked.
const BulkDownloadLabelsB2C = ({ embedded = false, caretX = null, parentColor = PRIMARY_BLUE, parentLabel = 'Label', parentIcon = <LabelOutlinedIcon /> }) => {
    const methodsBulkUpload = useForm({
        resolver: yupResolver(Yup.object().shape({ orderFile: Yup.mixed().required("Please Select a File") })),
        defaultValues: { orderFile: null }
    });
    const { handleSubmit: handleSubmitBulkUpload, reset } = methodsBulkUpload;
    const [fileLink, setFileLink] = useState([])
    const [isUploadingFile, setIsUploadingFile] = useState(false)
    const [zipLink, setZipLink] = useState([])
    const [mergedPdf, setMergedPdf] = useState([])

    const onSubmitBulkUpload = async (data) => {
        setIsUploadingFile(true)
        try {
            let form = new FormData()
            form.append('file', data.orderFile[0])
            await postMultipart(`${apiConstants.BULK_DOWNLOAD_LABELS}?shipmentType=B2C`, form).then(response => {
                if (response.data && response.data['excel'] && response.data['excel'].startsWith('https')) {
                    setFileLink(response.data['excel']);
                    setZipLink(response.data['zipFile'])
                    setMergedPdf(response.data['mergedPdf'])
                    toast("Successfully Uploaded!", { type: 'success' })
                    reset({ orderFile: null })
                } else {
                    toast(response?.data[3962], { type: 'error' })
                }
            }).catch(err => { console.error(err); setIsUploadingFile(false); toast(err?.response?.data?.message || "Error uploading file", { type: 'error' }) });
        } catch (err) {
            const message = err?.response?.data?.message || err.message
            toast(message || "Error uploading file", { type: 'error' })
        }
        setIsUploadingFile(false)
    }

    // Section bodies (without the standalone step headers) so they can be reused as either
    // stacked sections (standalone) or sub-tab panels (embedded).
    const pincodeBody = (
        <>
            <Box sx={helpTextSx}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
                <Typography sx={helpTextTypeSx}>
                    <strong>How it works:</strong> Select a date, optionally specify a time window, enter the pickup pincode,
                    and download merged labels as a single PDF for all orders matching the criteria.
                </Typography>
            </Box>
            <LabelDownloadBulk />
        </>
    )

    const awbBody = (
        <>
            <Box sx={helpTextSx}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
                <Typography sx={helpTextTypeSx}>
                    <strong>How it works:</strong> Upload an XLSX file containing AWB numbers (one per row).
                    Labels will be generated and available for download as Excel, ZIP, or merged PDF.
                </Typography>
            </Box>

            <Box sx={{ ...alertStripSx(WARNING), mb: 2 }}>
                <WarningAmberIcon sx={{ fontSize: 16, color: WARNING }} />
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                    Maximum 500 AWBs can be uploaded at once.
                </Typography>
            </Box>

            <FormProvider methods={methodsBulkUpload} onSubmit={handleSubmitBulkUpload(onSubmitBulkUpload)}>
                <RHFFileField name="orderFile" accept={['xlsx']} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
                    <LoadingButton startIcon={<FileUploadIcon />} type="submit" loading={isUploadingFile}
                        variant="contained" size="small" sx={primaryBtnSx}>
                        Upload
                    </LoadingButton>
                    {fileLink?.length === 0 && (
                        <Button variant="outlined" size="small" sx={secondaryBtnSx}
                            href={"https://djslb35m04okm.cloudfront.net/generate_labels.csv"} download="Order Upload Sample"
                            startIcon={<UploadFileOutlined sx={{ fontSize: 18 }} />}>
                            Sample Download
                        </Button>
                    )}
                    {fileLink?.length > 0 && (
                        <Button variant="outlined" size="small" sx={secondaryBtnSx}
                            href={fileLink} download="Download Excel" startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}>
                            Download Excel
                        </Button>
                    )}
                    {zipLink?.length > 0 && (
                        <Button variant="outlined" size="small" sx={secondaryBtnSx}
                            href={zipLink} download="Download Zip" startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}>
                            Download Zip
                        </Button>
                    )}
                    {mergedPdf?.length > 0 && (
                        <Button variant="outlined" size="small" sx={secondaryBtnSx}
                            href={mergedPdf} download="Download PDF" target="_blank" startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}>
                            Download Merged PDF
                        </Button>
                    )}
                </Box>
            </FormProvider>
        </>
    )

    // Embedded: two criteria become sub-tabs in a tray branching from the Label tab.
    if (embedded) {
        return (
            <NestedTabTray
                caretX={caretX}
                color={parentColor}
                parentLabel={parentLabel}
                parentIcon={parentIcon}
                tabs={[
                    { value: 'pincode', label: 'By Pincode & Date', icon: <EventOutlinedIcon />, render: () => pincodeBody },
                    { value: 'awb', label: 'By AWB Upload', icon: <CloudUploadOutlinedIcon />, render: () => awbBody },
                ]}
            />
        )
    }

    // Standalone route: hero + both criteria stacked as numbered sections.
    return (
        <Box sx={pageContainerSx}>
            <DownloadHero
                icon={<LocalPrintshopOutlinedIcon />}
                title="D2C Shipping Labels"
                subtitle="Download shipping labels by pincode & date, or upload an AWB list for bulk generation"
                chips={[
                    { icon: <QrCode2Icon />, label: 'By pincode or AWB' },
                    { icon: <PictureAsPdfOutlinedIcon />, label: 'Single merged PDF' },
                ]}
            />

            <Box sx={uploadSectionSx}>
                <SectionHead step={1} icon={<EventOutlinedIcon />} title="Download by Pincode & Date" desc="Merge all matching labels into one PDF" />
                {pincodeBody}
            </Box>

            <Box sx={uploadSectionSx}>
                <SectionHead step={2} icon={<CloudUploadOutlinedIcon />} title="Download by AWB Upload" desc="Upload an XLSX of AWBs to generate labels in bulk" accent={PURPLE} />
                {awbBody}
            </Box>
        </Box>
    )
}
export default withLayout(BulkDownloadLabelsB2C);
